import { useMemo, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Divider from '@mui/material/Divider'
import Link from '@mui/material/Link'
import Skeleton from '@mui/material/Skeleton'
import TablePagination from '@mui/material/TablePagination'
import Typography from '@mui/material/Typography'
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined'
import { useQuickInstruments } from '@/features/campaigns/api/quick-instruments-queries'
import {
  mapApiStatusToUiStatus,
  matchesDirectInstrumentFilter,
  matchesDirectInstrumentSearch,
  sortDirectInstrumentsByCreatedDesc,
  sortDirectInstrumentsByName,
  type DirectInstrumentStatusFilter,
} from '@/features/campaigns/lib/quick-instrument-display'
import { palettePrimaryMain } from '@/theme/designTokens'

import { ADMIN_DASHBOARD_LAYOUT } from '../lib/admin-dashboard-layout'
import { RECENT_CAMPAIGNS_TABLE } from '../lib/recent-campaigns-table-theme'
import { DashboardDirectInstrumentsTable } from './DashboardDirectInstrumentsTable'
import { DashboardDirectInstrumentsToolbar } from './DashboardDirectInstrumentsToolbar'

const DEFAULT_ROWS_PER_PAGE = 5
const ROWS_PER_PAGE_OPTIONS = [3, 5, 10, 15, 20] as const
const FETCH_PAGE_SIZE = 100

const paginationSx = {
  '& .MuiTablePagination-toolbar': {
    minHeight: ADMIN_DASHBOARD_LAYOUT.paginationMinHeight,
    px: 0,
  },
  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
    fontSize: 14,
    color: '#4a5565',
    letterSpacing: '-0.1504px',
  },
  '& .MuiTablePagination-select': {
    bgcolor: ADMIN_DASHBOARD_LAYOUT.paginationSelectBg,
    borderRadius: '4px',
  },
  '& .MuiTablePagination-actions .MuiIconButton-root': {
    color: '#4a5565',
  },
} as const

interface Props {
  /** When true, show link to Research → Direct tab for full management. */
  canMutate: boolean
}

function DirectInstrumentsTableSkeleton() {
  return (
    <Box
      aria-busy="true"
      aria-label="Loading direct instruments"
      data-testid="dashboard-direct-instruments-skeleton"
    >
      <Skeleton variant="rounded" height={48.5} sx={{ borderRadius: 0 }} />
      {Array.from({ length: 5 }, (_, index) => (
        <Box
          key={index}
          sx={{
            display: 'grid',
            gridTemplateColumns: '36% 18% 18% 28%',
            gap: 2,
            px: 3,
            height: 61,
            alignItems: 'center',
            borderBottom: '1px solid #e5e7eb',
          }}
        >
          <Skeleton variant="text" height={20} />
          <Skeleton variant="rounded" width={80} height={26} sx={{ borderRadius: 9999 }} />
          <Skeleton variant="text" height={20} />
          <Skeleton variant="rounded" width={34} height={34} sx={{ borderRadius: '6px' }} />
        </Box>
      ))}
    </Box>
  )
}

/**
 * BFP-999 TC-DIB16 / BR-DIB07 — Dashboard entry to instrument-level dashboards
 * for quick / direct-build instruments (no Campaign/LOI navigation).
 */
export function DashboardDirectInstrumentsSection({ canMutate }: Readonly<Props>) {
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<DirectInstrumentStatusFilter>('all')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE)
  const [nameSortDirection, setNameSortDirection] = useState<'asc' | 'desc' | null>(null)

  const { data, isLoading, isError, refetch } = useQuickInstruments({
    status: 'all',
    page: 1,
    pageSize: FETCH_PAGE_SIZE,
  })

  const items = data?.items ?? []
  const total = data?.total ?? 0

  const rows = useMemo(
    () =>
      items.map((item) => ({
        id: item.id,
        name: item.name,
        apiStatus: item.status,
        uiStatus: mapApiStatusToUiStatus(item.status),
        createdAt: item.createdAt,
      })),
    [items],
  )

  const filteredItems = useMemo(() => {
    let list = rows.filter(
      (row) =>
        matchesDirectInstrumentFilter(row.uiStatus, statusFilter) &&
        matchesDirectInstrumentSearch(row.name, search),
    )
    if (nameSortDirection) {
      list = sortDirectInstrumentsByName(list, nameSortDirection)
    } else {
      list = sortDirectInstrumentsByCreatedDesc(list)
    }
    return list
  }, [rows, statusFilter, search, nameSortDirection])

  const paginatedItems = useMemo(() => {
    const start = page * rowsPerPage
    return filteredItems.slice(start, start + rowsPerPage)
  }, [filteredItems, page, rowsPerPage])

  const handleToggleNameSort = () => {
    setNameSortDirection((prev) => {
      if (prev === null) return 'asc'
      if (prev === 'asc') return 'desc'
      return null
    })
    setPage(0)
  }

  const isEmpty = !isLoading && !isError && rows.length === 0
  const isFilterEmpty = !isLoading && !isError && rows.length > 0 && filteredItems.length === 0

  const renderTableContent = () => {
    if (isLoading) {
      return <DirectInstrumentsTableSkeleton />
    }

    if (isError) {
      return (
        <Box sx={{ py: 3, textAlign: 'center' }} data-testid="dashboard-direct-instruments-error">
          <Button
            variant="outlined"
            size="small"
            onClick={() => Promise.resolve(refetch()).catch(() => undefined)}
          >
            Retry
          </Button>
        </Box>
      )
    }

    if (isEmpty) {
      return (
        <Box
          sx={{ py: 6, textAlign: 'center', px: 3 }}
          data-testid="dashboard-direct-instruments-empty"
        >
          <ScienceOutlinedIcon sx={{ fontSize: 40, color: '#94a3b8', mb: 1.5 }} aria-hidden />
          <Typography sx={{ fontSize: 14, color: '#6b7280' }}>
            {canMutate
              ? 'No direct instruments yet. Use Build New Research to create one.'
              : 'No direct instruments to display.'}
          </Typography>
        </Box>
      )
    }

    if (isFilterEmpty) {
      return (
        <Box
          sx={{ py: 6, textAlign: 'center', px: 3 }}
          data-testid="dashboard-direct-instruments-filter-empty"
        >
          <Typography sx={{ fontSize: 14, color: '#6b7280' }}>
            No instruments match the current search or filter.
          </Typography>
        </Box>
      )
    }

    return (
      <DashboardDirectInstrumentsTable
        rows={paginatedItems}
        nameSortDirection={nameSortDirection}
        onToggleNameSort={handleToggleNameSort}
      />
    )
  }

  return (
    <Box data-testid="dashboard-direct-instruments" sx={{ mt: ADMIN_DASHBOARD_LAYOUT.metricsToRecentGap }}>
      <Divider
        data-testid="dashboard-direct-instruments-divider"
        sx={{
          mb: ADMIN_DASHBOARD_LAYOUT.metricsToRecentGap,
          borderColor: RECENT_CAMPAIGNS_TABLE.rowBorder,
        }}
      />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          mb: ADMIN_DASHBOARD_LAYOUT.recentSectionHeaderGap,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 600,
                lineHeight: '28px',
                color: ADMIN_DASHBOARD_LAYOUT.recentTitleColor,
                letterSpacing: ADMIN_DASHBOARD_LAYOUT.recentTitleLetterSpacing,
              }}
            >
              Direct Instruments
            </Typography>
            <Link
              component={RouterLink}
              to="/campaigns?tab=direct"
              underline="hover"
              data-testid="dashboard-direct-instruments-view-all"
              sx={{
                fontSize: 14,
                fontWeight: 500,
                color: palettePrimaryMain,
                whiteSpace: 'nowrap',
              }}
            >
              View all{total > 0 ? ` (${total})` : ''}
            </Link>
          </Box>
          <Typography sx={{ fontSize: 13, color: '#64748b', mt: 0.25 }}>
            Quick-built instruments — open the instrument dashboard without a Campaign or LOI
          </Typography>
        </Box>

        <DashboardDirectInstrumentsToolbar
          search={search}
          onSearchChange={(value) => {
            setSearch(value)
            setPage(0)
          }}
          statusFilter={statusFilter}
          onStatusFilterChange={(value) => {
            setStatusFilter(value)
            setPage(0)
          }}
        />
      </Box>

      <Card
        elevation={0}
        sx={{
          bgcolor: RECENT_CAMPAIGNS_TABLE.bodyBg,
          border: `1px solid ${RECENT_CAMPAIGNS_TABLE.cardBorder}`,
          borderRadius: RECENT_CAMPAIGNS_TABLE.cardRadius,
          overflow: 'hidden',
        }}
      >
        {renderTableContent()}
      </Card>

      {!isLoading && !isError && filteredItems.length > 0 && (
        <TablePagination
          component="div"
          count={filteredItems.length}
          page={page}
          onPageChange={(_e, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(Number.parseInt(e.target.value, 10))
            setPage(0)
          }}
          rowsPerPageOptions={[...ROWS_PER_PAGE_OPTIONS]}
          data-testid="dashboard-direct-instruments-pagination"
          sx={paginationSx}
        />
      )}
    </Box>
  )
}
